import React, {useEffect, useRef, useState} from 'react';
import paper from 'paper';
import {FilesetResolver, ImageSegmenter} from '@mediapipe/tasks-vision';
import ColorVectorizer from "./vectorizer";

const CameraSegmenterPaperJS = () => {
    // Refs
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const segmenterRef = useRef(null);

    // State
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isModelLoaded, setIsModelLoaded] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // 1. Initialize Paper.js & MediaPipe Model
    useEffect(() => {
        // Setup Paper.js
        paper.setup(canvasRef.current);

        // Load AI Model
        const loadModel = async () => {
            try {
                const vision = await FilesetResolver.forVisionTasks(
                    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
                );

                segmenterRef.current = await ImageSegmenter.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite",
                        delegate: "GPU"
                    },
                    // We use IMAGE mode because we only segment when the user clicks the button
                    runningMode: "IMAGE",
                    outputCategoryMask: true,
                    outputConfidenceMasks: false
                });

                setIsModelLoaded(true);
            } catch (error) {
                console.error("Error loading MediaPipe model:", error);
            }
        };

        loadModel();

        return () => {
            paper.project.clear();
            if (segmenterRef.current) {
                segmenterRef.current.close();
            }
        };
    }, []);

    // 2. Camera Controls
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setIsCameraOn(true);
        } catch (err) {
            console.error("Camera access denied: ", err);
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsCameraOn(false);
    };

    // Cleanup camera if unmounted
    useEffect(() => stopCamera, []);

    // 3. Grab Image, Segment, and Draw to Paper.js
    const grabAndSegment = async () => {
        const video = videoRef.current;
        if (!video || !isCameraOn || !segmenterRef.current) return;

        setIsProcessing(true);

        // Clear previous Paper.js items
        paper.project.activeLayer.removeChildren();

        // Create an off-screen canvas to hold the video frame
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = video.videoWidth;
        tempCanvas.height = video.videoHeight;
        const ctx = tempCanvas.getContext('2d');

        // Draw the raw snapshot
        ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);

        // Run MediaPipe segmentation on the static canvas
        const result = segmenterRef.current.segment(tempCanvas);

        if (result.categoryMask) {
            const mask = result.categoryMask.getAsUint8Array();
            const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const pixels = imageData.data;



            // Iterate through the pixels to apply color overlays based on category
            for (let i = 0; i < mask.length; i++) {
                const category = mask[i];
               // Background - leave original pixels alone
                const alpha = category === 0 ? 1 : 0.2; // Opacity of the color overlay (0.0 to 1.0)
                const invAlpha = 1 - alpha;
                const pixelIndex = i * 4;
                let r, g, b;

                switch (category) {
                    case 0: // Hair (Red)
                        r = 255; g = 255; b = 255; break;
                    case 1: // Hair (Red)
                        r = 255; g = 50; b = 50; break;
                    case 2: // Body Skin (Green)
                    case 3: // Face Skin (Green)
                        r = 50; g = 255; b = 50; break;
                    case 4: // Clothes (Blue)
                        r = 50; g = 100; b = 255; break;
                    case 5: // Accessories (Yellow)
                        r = 255; g = 255; b = 50; break;
                    default:
                        continue;
                }

                // Blend the color overlay with the original camera pixel underneath
                pixels[pixelIndex] = (pixels[pixelIndex] * invAlpha) + (r * alpha);         // Red
                pixels[pixelIndex + 1] = (pixels[pixelIndex + 1] * invAlpha) + (g * alpha); // Green
                pixels[pixelIndex + 2] = (pixels[pixelIndex + 2] * invAlpha) + (b * alpha); // Blue
                // Alpha channel (pixels[pixelIndex + 3]) is untouched
            }

            // Put the newly colored pixels back onto the off-screen canvas
            ctx.putImageData(imageData, 0, 0);
        }

        // Pass the colored canvas into Paper.js
        const snapshotRaster = new paper.Raster(tempCanvas);

        snapshotRaster.onLoad = () => {
            // Center and scale to fill the Paper.js canvas
            snapshotRaster.position = paper.view.center;

            const scaleX = paper.view.size.width / snapshotRaster.width;
            const scaleY = paper.view.size.height / snapshotRaster.height;

            snapshotRaster.scale(Math.max(scaleX, scaleY));
            setIsProcessing(false);
        };
    };

    return (
        <>
        <div style={{ display: 'flex', gap: '40px', padding: '20px', fontFamily: 'sans-serif' }}>

            {/* Left Column: Camera Viewfinder & Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '320px' }}>
                <h3>1. Camera Viewfinder</h3>

                <div style={{
                    width: '320px',
                    height: '240px',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    {!isModelLoaded && (
                        <div style={overlayStyle}>Loading AI Model...</div>
                    )}
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    {!isCameraOn ? (
                        <button onClick={startCamera} disabled={!isModelLoaded} style={btnStyle(isModelLoaded ? '#4CAF50' : '#888')}>
                            Start Camera
                        </button>
                    ) : (
                        <button onClick={stopCamera} style={btnStyle('#f44336')}>
                            Stop Camera
                        </button>
                    )}
                </div>

                <button
                    onClick={grabAndSegment}
                    disabled={!isCameraOn || isProcessing}
                    style={{
                        ...btnStyle('#2196F3'),
                        opacity: (!isCameraOn || isProcessing) ? 0.5 : 1,
                        cursor: (!isCameraOn || isProcessing) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isProcessing ? 'Processing AI Mask...' : '📸 Grab & Segment Frame'}
                </button>

                <div style={legendStyle}>
                    <div><span style={{color: '#ff3232'}}>■</span> Hair</div>
                    <div><span style={{color: '#32ff32'}}>■</span> Skin</div>
                    <div><span style={{color: '#3264ff'}}>■</span> Clothes</div>
                    <div><span style={{color: '#ffff32'}}>■</span> Accessories</div>
                </div>
            </div>

            {/* Right Column: Paper.js Workspace */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <h3>2. Segmented Result (Paper.js)</h3>
                <canvas
                    ref={canvasRef}
                    width={800}
                    height={600}
                    style={{
                        backgroundColor: '#222',
                        borderRadius: '12px',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                    }}
                />
            </div>

        </div>
            <hr/>
            <ColorVectorizer sourceElement={canvasRef.current} tolerance={30} />

        </>
    );
};

const btnStyle = (bgColor) => ({
    flex: 1,
    padding: '12px 15px',
    backgroundColor: bgColor,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 'bold',
    cursor: 'pointer',
});

const overlayStyle = {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    fontWeight: 'bold',
    zIndex: 10
};

const legendStyle = {
    backgroundColor: '#f5f5f5',
    padding: '10px',
    borderRadius: '8px',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between'
};

export default CameraSegmenterPaperJS;